import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Card, CardBody, Chip } from '@nextui-org/react';
import { CalendarClock, Crown, Gamepad2, Lock, User as UserIcon, Users } from 'lucide-react';
import communityService from '../../services/communityService';
import lobbyService, { Lobby } from '../../services/lobbyService';

const ACCENT = '#3fced1';

/** Avatare-Stack der Mitglieder (max. 4 sichtbar + "+x") */
export const LobbyMemberStack: React.FC<{ lobby: Lobby; size?: 'sm' | 'md' }> = ({
  lobby,
  size = 'sm',
}) => {
  const members = Array.isArray(lobby.members) ? lobby.members : [];
  const visible = members.slice(0, 4);
  const rest = members.length - visible.length;
  const dim = size === 'md' ? 'w-8 h-8' : 'w-6 h-6';
  const iconSize = size === 'md' ? 16 : 12;

  if (members.length === 0) return null;

  return (
    <div className="flex items-center">
      <div className="flex -space-x-2">
        {visible.map((member, idx) => {
          const avatarUrl = communityService.buildThumbnailUrl(member.userImage, 100);
          return (
            <div
              key={member.userId ?? idx}
              className={`${dim} rounded-full ring-2 ring-zinc-900 bg-zinc-800 overflow-hidden flex items-center justify-center flex-shrink-0`}
              title={member.userName ?? undefined}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={member.userName ?? ''}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <UserIcon size={iconSize} className="text-gray-500" />
              )}
            </div>
          );
        })}
      </div>
      {rest > 0 && (
        <span className="ml-1.5 text-[11px] font-semibold text-gray-400">+{rest}</span>
      )}
    </div>
  );
};

/** Status-Badge (Offen/Voll/Geschlossen) */
export const LobbyStatusBadge: React.FC<{ lobby: Lobby }> = ({ lobby }) => {
  const color = lobbyService.getStatusColor(lobby.status);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
      style={{ backgroundColor: `${color}1A`, color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      {lobbyService.getStatusLabel(lobby.status)}
    </span>
  );
};

const LobbyCard: React.FC<{ lobby: Lobby }> = ({ lobby }) => {
  const memberCount = lobbyService.getMemberCount(lobby);
  const maxMembers = lobby.maxMembers ?? 8;
  const scheduled = lobbyService.formatScheduledAt(lobby.scheduledAt);
  const tags = Array.isArray(lobby.tags) ? lobby.tags.slice(0, 3) : [];

  return (
    <RouterLink
      to={`/community/lobby/${encodeURIComponent(lobby.id ?? '')}`}
      className="block group h-full"
      aria-label={`Lobby ${lobby.name ?? ''} ansehen`}
    >
      <Card
        className="bg-zinc-900 border border-zinc-800 w-full h-full transition-all duration-300 group-hover:border-[#3fced1]/60 group-hover:-translate-y-1 group-hover:shadow-[0_12px_32px_-8px_rgba(63,206,209,0.25)]"
        shadow="sm"
      >
        <CardBody className="p-4 flex flex-col gap-3">
          {/* Kopf: Status + Modus */}
          <div className="flex items-center justify-between gap-2">
            <LobbyStatusBadge lobby={lobby} />
            {lobby.isPublic === false && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500">
                <Lock size={11} /> Privat
              </span>
            )}
          </div>

          {/* Name */}
          <div>
            <p className="text-base font-bold text-white leading-snug line-clamp-2 transition-colors group-hover:text-[#3fced1]">
              {lobby.name ?? 'Unbenannte Lobby'}
            </p>
            {lobby.description && (
              <p className="mt-1 text-xs text-gray-400 line-clamp-2">{lobby.description}</p>
            )}
          </div>

          {/* Badges: GameMode + Plattform + Tags */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {lobby.gameMode && (
              <Chip
                size="sm"
                variant="flat"
                startContent={<Gamepad2 size={12} style={{ color: ACCENT }} />}
                classNames={{
                  base: 'h-6 px-2 border',
                  content: 'text-[11px] font-semibold px-1',
                }}
                style={{
                  backgroundColor: `${ACCENT}14`,
                  borderColor: `${ACCENT}33`,
                  color: ACCENT,
                }}
              >
                {lobby.gameMode}
              </Chip>
            )}
            {lobby.platform && (
              <Chip
                size="sm"
                variant="flat"
                classNames={{
                  base: 'h-6 px-2 bg-zinc-800 border border-zinc-700',
                  content: 'text-[11px] text-gray-300 px-1',
                }}
              >
                {lobby.platform}
              </Chip>
            )}
            {tags.map((tag) => (
              <Chip
                key={tag}
                size="sm"
                variant="flat"
                classNames={{
                  base: 'h-6 px-2 bg-zinc-800 border border-zinc-700',
                  content: 'text-[11px] text-gray-400 px-1',
                }}
              >
                #{tag}
              </Chip>
            ))}
          </div>

          {/* Fuß: Mitglieder + Zeit + Owner */}
          <div className="mt-auto pt-2 border-t border-zinc-800 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                <Users size={13} style={{ color: ACCENT }} />
                <span className="font-semibold text-white">{memberCount}</span>/{maxMembers}
              </span>
              <LobbyMemberStack lobby={lobby} />
            </div>
            {scheduled && (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                <CalendarClock size={13} style={{ color: ACCENT }} />
                {scheduled}
              </span>
            )}
            {lobby.ownerName && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                <Crown size={12} className="text-amber-400" />
                {lobby.ownerName}
              </span>
            )}
          </div>
        </CardBody>
      </Card>
    </RouterLink>
  );
};

export default LobbyCard;
